/**
 * First-party member activity log (page views + clicks) → POST /api/members/activity.
 * Sender is registered by MemberActivityReporter when the user is authenticated.
 */

const MAX_QUEUE = 200;
const FLUSH_INTERVAL_MS = 2000;
const MAX_BATCH = 30;

let sendBatch = null;
let queue = [];
let flushTimer = null;

function currentPath() {
  if (typeof window === 'undefined') return null;
  return window.location.pathname || '/';
}

function scheduleFlush() {
  if (flushTimer || !sendBatch) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushActivityQueue();
  }, FLUSH_INTERVAL_MS);
}

export function setMemberActivitySender(fn) {
  sendBatch = typeof fn === 'function' ? fn : null;
  if (sendBatch && queue.length > 0) {
    scheduleFlush();
  }
}

export async function flushActivityQueue() {
  if (!sendBatch || queue.length === 0) return;
  while (queue.length > 0) {
    const batch = queue.splice(0, MAX_BATCH);
    try {
      await sendBatch(batch);
    } catch (err) {
      console.warn('[memberActivity] flush failed', err);
      break;
    }
  }
}

export function enqueueMemberActivity(event) {
  if (!event?.kind || !event?.action) return;
  if (!sendBatch) return;

  const last = queue[queue.length - 1];
  if (
    last &&
    last.kind === event.kind &&
    last.action === event.action &&
    last.path === event.path &&
    Date.now() - (last._ts || 0) < 400
  ) {
    return;
  }

  queue.push({ ...event, _ts: Date.now() });
  if (queue.length > MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE);
  }
  scheduleFlush();
}

export function logMemberPageView(path) {
  const pathname = path || currentPath();
  if (!pathname) return;
  enqueueMemberActivity({
    kind: 'page_view',
    action: pathname,
    path: pathname,
  });
}

export function logMemberClick(action, category, label) {
  if (!action) return;
  enqueueMemberActivity({
    kind: 'click',
    action: String(action),
    path: currentPath(),
    category: category || null,
    label: label != null ? String(label) : null,
  });
}

export function startMemberActivityFlushInterval() {
  if (typeof window === 'undefined') return undefined;
  const id = setInterval(() => {
    void flushActivityQueue();
  }, FLUSH_INTERVAL_MS);
  const onUnload = () => {
    void flushActivityQueue();
  };
  window.addEventListener('pagehide', onUnload);
  return () => {
    clearInterval(id);
    window.removeEventListener('pagehide', onUnload);
  };
}
